import { FC, PropsWithChildren, useContext, useEffect } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext, useUserContext } from '@editor/contexts/Users'
import MeshTileGltf from '@editor/pages/projects/common/SolidMeshManager/threejs/MeshTile/Gltf'
import { getFeatureFlags, getServiceStatus, getTutorialVideoConfig } from '@editor/services/App'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setFeatureFlags, setFeatureFlagsLoaded } from '@editor/store/featureFlags'
import { setServiceStatus } from '@editor/store/system'
import { setVideos } from '@editor/store/video'
import { Toaster } from '@editor/system/toaster'
import { MeshTileObj, MeshTilesManager } from '@infraloop/common'
import { RouteProjectsLayout } from '@infraloop/editor'
import { extend } from '@react-three/fiber'
import { useSelector } from 'react-redux'
import { Line2, LineMaterial } from 'three-stdlib'

import GlobalModal from './GlobalModal'

// The 3D editor renders these custom three.js objects declaratively; they must be
// registered with react-three-fiber before any editor canvas mounts (mirrors app/editor).
extend({ Line2, LineMaterial, MeshTileObj, MeshTileGltf, MeshTilesManager })

/**
 * Seeds the editor's Redux store with the data its body expects — service status, feature
 * flags and tutorial videos. Without service status the editor's
 * user-loading is gated off, so this runs before the editor pages render. Mirrors the
 * bootstrapping `app/editor` does in its own shell; the dashboard-level ToS and subscription
 * chrome is intentionally left out — Hatsuly's dashboard owns those.
 */
const EditorBootstrap: FC<PropsWithChildren> = ({ children }) => {
  const { isLoading, user, getAccessTokenSilently } = useAuth0()
  const dispatch = useAppDispatch()
  const serviceStatus = useSelector((state: RootState) => state.system.serviceStatus)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Service status
  useEffect(() => {
    if (isLoading) return

    void (async () => {
      const token = user ? await getAccessTokenSilently() : undefined
      const status = await getServiceStatus(showErrorModal, token)
      if (status) dispatch(setServiceStatus(status))
    })()
  }, [showErrorModal, getAccessTokenSilently, user, isLoading, dispatch])

  // Tutorial video config
  useEffect(() => {
    if (!user || isLoading || !serviceStatus || serviceStatus.isDown.enabled) return

    void (async () => {
      const token = await getAccessTokenSilently()
      if (!token) return

      const videoConfig = await getTutorialVideoConfig(showErrorModal, token)
      if (videoConfig) dispatch(setVideos(videoConfig))
    })()
  }, [showErrorModal, getAccessTokenSilently, serviceStatus, user, isLoading, dispatch])

  // Feature flags
  useEffect(() => {
    if (isLoading || !serviceStatus || serviceStatus.isDown.enabled) return

    void (async () => {
      const featureFlags = await getFeatureFlags(showErrorModal)
      if (featureFlags) dispatch(setFeatureFlags(featureFlags))
      dispatch(setFeatureFlagsLoaded())
    })()
  }, [showErrorModal, serviceStatus, isLoading, dispatch])

  return children
}

/** Loads the signed-in user into the editor store and exposes the editor's user context. */
const EditorUserProvider: FC<PropsWithChildren> = ({ children }) => {
  const userContextVal = useUserContext()
  return <UserContext.Provider value={userContextVal}>{children}</UserContext.Provider>
}

/**
 * Layout route that hosts the shared editor body (`@infraloop/editor`) inside `app/hatsuly`.
 * It supplies the app-chrome contexts the editor body relies on — the confirm/error modal
 * and the user context — that live outside the package, then renders the matched editor page
 * through {@link RouteProjectsLayout}'s outlet. The editor's router context is provided app-
 * wide in `App.tsx`, and the Hatsuly-specific editor header via `EditorProvider` in `main.tsx`.
 */
const EditorHost: FC = () => (
  <GlobalModal>
    <EditorBootstrap>
      <EditorUserProvider>
        <RouteProjectsLayout />
      </EditorUserProvider>
    </EditorBootstrap>
    <Toaster />
  </GlobalModal>
)

export default EditorHost
